// PWA Configuration - Easy Customization Guide
// Edit this to customize your PWA settings

// ============================================
// SERVICE WORKER CACHE CONFIGURATION
// ============================================

// Cache name - increment version when you want to clear old cache
// Change from 'v1' to 'v2' to force cache refresh for all users
const CACHE_VERSION = 'v1';
const CACHE_NAME = `cogent-app-${CACHE_VERSION}`;

// URLs to cache immediately on service worker install
// Add any critical resources that should always be offline-available
const urlsToCache = [
  '/',
  '/index.html',
  '/favicon.ico',
  '/logo192.png',
  '/logo512.png',
  '/manifest.json'
  // Add more critical paths here if needed
];



// ============================================
// MANIFEST CONFIGURATION
// ============================================

// Update in public/manifest.json:
const MANIFEST_CONFIG = {
  name: "Cogent - Order Management",
  short_name: "Cogent",
  description: "Modern quotation and invoice management system",
  
  // Theme colors (RGB)
  theme_color: "#16a34a",      // Orange - shown in browser UI
  background_color: "#ffffff",  // White
  
  // Display modes: 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser'
  display: "standalone",
  
  // Orientation: 'portrait' | 'landscape' | 'portrait-primary'
  orientation: "portrait-primary",
  
  // Icon sizes needed:
  // - 192x192: Home screen icon
  // - 512x512: Splash screen
  icons: [
    // {
    //   src: "favicon.ico",
    //   sizes: "64x64 32x32 24x24 16x16"
    // },
    {
      src: "logo192.png",
      sizes: "192x192",
      purpose: "any"
    },
    {
      src: "logo512.png",
      sizes: "512x512",
      purpose: "maskable"
    }
  ]
};

// ============================================
// APP SHORTCUTS (Quick access from home screen)
// ============================================

const SHORTCUTS = [
  {
    name: "Orders",
    short_name: "Orders",
    description: "View and manage orders",
    url: "/panel/orders",
    icons: [{ src: "logo192.png", sizes: "192x192" }]
  },
  {
    name: "Invoices",
    short_name: "Invoices",
    description: "View and manage invoices",
    url: "/panel/invoices",
    icons: [{ src: "logo192.png", sizes: "192x192" }]
  }
  // Add more shortcuts here
];

// ============================================
// OFFLINE BEHAVIOR CONFIGURATION
// ============================================

const OFFLINE_CONFIG = {
  // Show offline banner
  showOfflineBanner: true,
  
  // Banner position: 'top' | 'bottom'
  bannerPosition: 'top',
  
  // Banner color (Tailwind class)
  bannerColor: 'bg-orange-500',
  
  // Cache strategy for different request types
  cacheStrategy: 'network-first', // 'network-first' | 'cache-first' | 'stale-while-revalidate'
  
  // Cache timeout in milliseconds (for network-first strategy)
  networkTimeout: 5000,
  
  // Which requests to exclude from caching
  noCachePatterns: [
    '/api/',           // API calls (handle separately)
    '/auth/',          // Auth requests (sensitive)
    '.map'             // Source maps
  ]
};

// ============================================
// STORAGE CONFIGURATION
// ============================================

const STORAGE_CONFIG = {
  // Maximum items to store in localStorage
  maxStorageSize: 5242880, // 5MB in bytes
  
  // Keys for offline data
  storageKeys: {
    pendingOrders: 'pending-orders',
    pendingInvoices: 'pending-invoices',
    userPreferences: 'user-prefs',
    cachedQuotations: 'cached-quotations'
  },
  
  // Auto-clear old data after (in days)
  clearDataAfterDays: 30
};

// ============================================
// NOTIFICATION CONFIGURATION (Future)
// ============================================

const NOTIFICATION_CONFIG = {
  // Enable push notifications
  enabled: false, // Set to true to enable
  
  // Notification titles
  titles: {
    orderCreated: 'Order Created',
    orderUpdated: 'Order Updated',
    invoiceGenerated: 'Invoice Generated',
    syncComplete: 'Changes Synced'
  },
  
  // Notification badge
  badge: 'logo192.png',
  
  // Notification icon
  icon: 'logo192.png'
};

// ============================================
// COLOR SCHEME CONFIGURATION
// ============================================

const COLORS = {
  primary: '#EA580C',      // Orange
  secondary: '#ffffff',    // White
  error: '#ef4444',        // Red
  success: '#10b981',      // Green
  warning: '#f59e0b',      // Amber
  info: '#3b82f6'          // Blue
};

// ============================================
// API CONFIGURATION
// ============================================

const API_CONFIG = {
  baseURL: 'https://app.cogenttechsolutions.info/api/v1/api/v1/',
  timeout: 5000,
  retryAttempts: 3,
  retryDelay: 1000, // milliseconds
  
  // Endpoints that should be cached
  cacheableEndpoints: [
    '/products',
    '/users',
    '/colors'
  ],
  
  // Endpoints that should NOT be cached
  nonCacheableEndpoints: [
    '/auth/login',
    '/auth/logout',
    '/auth/refresh'
  ]
};

// ============================================
// CACHING STRATEGY EXAMPLES
// ============================================

/*
NETWORK-FIRST (Current - Recommended for most cases)
- Try network first
- If network fails, use cache
- Best for: API calls, user data
- Pro: Always fresh data when online
- Con: Slower if network is slow

CACHE-FIRST
- Check cache first
- If not in cache, fetch from network
- Best for: Static assets, images
- Pro: Faster load time
- Con: May be stale

STALE-WHILE-REVALIDATE
- Return cached version immediately
- Fetch fresh version in background
- Update cache with fresh version
- Best for: Content that can be slightly outdated
- Pro: Always fast, always fresh eventually
- Con: More complex logic

To change strategy, edit public/service-worker.js fetch event
*/

// ============================================
// DEVELOPMENT HELPERS
// ============================================

// Uncomment in development to monitor PWA
const DEV_CONFIG = {
  logServiceWorker: true,      // Log SW registration
  logCacheEvents: true,        // Log cache hits/misses
  logStorageEvents: true,      // Log storage operations
  logNetworkStatus: true,      // Log online/offline changes
  clearCacheOnStart: false     // Clear cache when app starts (dev only)
};

// ============================================
// ENVIRONMENT-SPECIFIC SETTINGS
// ============================================

const getEnvConfig = () => {
  const isDev = process.env.NODE_ENV === 'development';
  const isProd = process.env.NODE_ENV === 'production';
  
  return {
    isDev,
    isProd,
    apiURL: isProd ? 'https://app.cogenttechsolutions.info/api/v1/'
    : 'https://app.cogenttechsolutions.info/api/v1/',
    swPath: isProd ? '/service-worker.js' : '/service-worker.js',
    cacheVersion: isProd ? 'v1' : 'dev'
  };
};

// ============================================
// CUSTOM CACHING LOGIC EXAMPLE
// ============================================

/*
// Cache specific content types differently
function getCacheStrategy(request) {
  const url = new URL(request.url);
  
  // Images - cache first
  if (/\.(jpg|jpeg|png|gif|svg)$/.test(url.pathname)) {
    return 'cache-first';
  }
  
  // API - network first
  if (url.pathname.startsWith('/api/')) {
    return 'network-first';
  }
  
  // HTML - stale-while-revalidate
  if (request.headers.get('accept').includes('text/html')) {
    return 'stale-while-revalidate';
  }
  
  return 'network-first'; // default
}
*/

// ============================================
// EXPORT FOR USE IN OTHER FILES
// ============================================

export {
  CACHE_VERSION,
  CACHE_NAME,
  urlsToCache,
  OFFLINE_CONFIG,
  STORAGE_CONFIG,
  NOTIFICATION_CONFIG,
  COLORS,
  API_CONFIG,
  DEV_CONFIG,
  getEnvConfig
};

export default {
  CACHE_VERSION,
  OFFLINE_CONFIG,
  STORAGE_CONFIG,
  NOTIFICATION_CONFIG,
  COLORS,
  API_CONFIG,
  DEV_CONFIG
};


// export const API_BASE_URL = "http://app.cogenttechsolutions.info/api/v1/";
//  export const API_BASE_URL = "https://app.cogenttechsolutions.info/api/v1/";

const isLocalHost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

/** Local dev uses localhost API; production builds must hit the deployed API (not localhost). */
export const API_BASE_URL = isLocalHost
  ? "http://localhost:30041/api/v1/"
  : "https://app.cogenttechsolutions.info/api/v1/";
